/* eslint-disable max-lines */
import React, { useEffect, useState } from 'react'
import { Form, Field } from 'react-final-form'
import { FormApi } from 'final-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import FormAutocomplete from '../atoms/FormAutocomplete'
import FormSelect from '../atoms/FormSelect'
import FormSuffixInput from '../atoms/FormSuffixInput'
import { FormFieldError, FormFieldLabel, FormTextInput } from '../atoms/FormTextInput'
import InsetSegmentedControl from '../atoms/InsetSegmentedControl'
import FormFooter from './FormFooter'
import VehicleEquipmentFields from './VehicleEquipmentFields'
import InfoCircleSvg from '../other/svg/InfoCircleSvg'
import PlusSvg from '../other/svg/PlusSvg'
import PurchasedOffersPicker from './PurchasedOffersPicker'
import { Brand, FETCH_BRANDS } from 'src/redux/brandsReducer'
import { MarketplaceOffer } from 'src/redux/marketplaceOffersReducer'
import { CLEAR_MOTORBIKE_MODELS, FETCH_MOTORBIKE_MODELS } from 'src/redux/motorbikeModelsReducer'
import { RootReducer } from 'src/redux/rootReducer'
import { MotorbikeFormData } from '../organisms/MotorbikeCreate'

type VehicleSource = 'purchased' | 'own'

interface VehicleCreateFormValues extends Omit<MotorbikeFormData, 'drive_type' | 'gear_box' | 'next_hu'> {
  year: string
  drive_type: string
  gear_box: string
  next_hu: string
}

const monthFormValue = (value: string | null): string => {
  if (value === null || value.length < 7) {
    return ''
  }
  return value.slice(0, 7)
}

const monthPayload = (value: string): string | null => {
  if (!/^\d{4}-(?:0[1-9]|1[0-2])$/.test(value)) {
    return null
  }
  return `${value}-01`
}

const emptyToNull = (value: string): string | null => {
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}

interface VehicleCreateFormProps {
  initialValues: MotorbikeFormData
  onSubmit: (values: MotorbikeFormData) => void
  onCancel: () => void
  mode?: 'create' | 'edit'
}

const currentYear = new Date().getFullYear()
const yearOptions = Array.from({ length: currentYear - 1979 }, (_, index) => {
  const year = (currentYear - index).toString()
  return { value: year, label: year }
})

const yearFromDate = (value: string | null): string => {
  return value === null || value.length < 4 ? '' : value.slice(0, 4)
}

const hasPurchasedOfferId = (railsAppId: string | null | undefined): boolean => {
  return (railsAppId ?? '').toString().length > 0
}

const INTEGER_PATTERN = /^\d+$/

const blankToNull = (value: string | null | undefined): string | null => {
  const trimmed = (value ?? '').trim()
  return trimmed === '' ? null : trimmed
}

const optionalIntegerError = (value: string | null | undefined): string | null => {
  const trimmed = (value ?? '').trim()
  if (trimmed !== '' && !INTEGER_PATTERN.test(trimmed)) {
    return 'integer'
  }
  return null
}

const validateCreateForm = (values: VehicleCreateFormValues): Record<string, string> => {
  const errors: Record<string, string> = {}
  if (Number(values.brand_id) === 0) {
    errors.brand_id = 'required'
  }
  if ((values.model ?? '').trim().length === 0) {
    errors.model = 'required'
  }
  if ((values.year ?? '').length === 0) {
    errors.year = 'required'
  }
  if ((values.total_mileage ?? '').trim().length === 0) {
    errors.total_mileage = 'required'
  }
  if ((values.vin ?? '').trim().length === 0) {
    errors.vin = 'required'
  }
  const capacityError = optionalIntegerError(values.capacity)
  const powerError = optionalIntegerError(values.engine_power)
  const ownersError = optionalIntegerError(values.number_of_previous_owners)
  if (typeof capacityError === 'string') {
    errors.capacity = capacityError
  }
  if (typeof powerError === 'string') {
    errors.engine_power = powerError
  }
  if (typeof ownersError === 'string') {
    errors.number_of_previous_owners = ownersError
  }
  return errors
}

interface FieldMeta {
  error?: string
  touched?: boolean
  submitFailed?: boolean
}

interface MotorbikeModelsFetcherProps {
  brandId: number
  brands: Brand[]
}

const isFieldInvalid = (meta: FieldMeta): boolean =>
  Boolean(meta.error) && (Boolean(meta.touched) || Boolean(meta.submitFailed))

const fieldClass = 'self-stretch flex flex-col justify-start items-start gap-2'

interface IntegerFieldProps {
  name: 'capacity' | 'engine_power' | 'number_of_previous_owners'
  label: string
  suffix?: string
}

const IntegerField = ({ name, label, suffix }: IntegerFieldProps): JSX.Element => {
  const { t } = useTranslation()
  return (
    <Field name={name}>
      {({ input, meta }) => (
        <div className={fieldClass}>
          <FormFieldLabel label={label} />
          {typeof suffix === 'string' ? (
            <FormSuffixInput
              value={input.value ?? ''}
              onChange={input.onChange}
              placeholder=""
              suffix={suffix}
              error={isFieldInvalid(meta)}
            />
          ) : (
            <FormTextInput
              value={input.value ?? ''}
              onChange={input.onChange}
              placeholder=""
              error={isFieldInvalid(meta)}
            />
          )}
          <FormFieldError show={isFieldInvalid(meta)} message={t('my_vehicles.field_integer')} />
        </div>
      )}
    </Field>
  )
}
const MotorbikeModelsFetcher = ({ brandId, brands }: MotorbikeModelsFetcherProps): null => {
  const dispatch = useDispatch()

  useEffect(() => {
    const brand = brands.find((item) => item.id === brandId) ?? null
    if (brand === null) {
      dispatch(CLEAR_MOTORBIKE_MODELS())
      return
    }
    dispatch(FETCH_MOTORBIKE_MODELS(brand.rails_app_id))
  }, [brandId, brands, dispatch])

  return null
}

const VehicleCreateForm = ({
  initialValues,
  onSubmit,
  onCancel,
  mode = 'create'
}: VehicleCreateFormProps): JSX.Element => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { brands } = useSelector((state: RootReducer) => state.brandsReducer)
  const motorbikeModels = useSelector((state: RootReducer) => state.motorbikeModelsReducer.models)
  const [source, setSource] = useState<VehicleSource>('own')
  const [selectedOffer, setSelectedOffer] = useState<MarketplaceOffer | null>(null)

  useEffect(() => {
    dispatch(FETCH_BRANDS())
  }, [dispatch])

  const brandOptions = brands.map((brand: Brand) => ({
    value: brand.id.toString(),
    label: brand.name
  }))

  const handleSubmitForm = (values: VehicleCreateFormValues): void => {
    const { year, drive_type, gear_box, next_hu, ...motorbikeValues } = values
    const fromPurchased = source === 'purchased' && hasPurchasedOfferId(values.rails_app_id)
    const keepLinkedOffer = mode === 'edit' || fromPurchased
    const brandName = brands.find((brand) => brand.id === Number(values.brand_id))?.name ?? ''
    onSubmit({
      ...motorbikeValues,
      motorbike_name: `${brandName} ${values.model}`.trim(),
      rails_app_id: keepLinkedOffer ? values.rails_app_id : null,
      rails_app_hash_id: keepLinkedOffer ? values.rails_app_hash_id : null,
      first_registration: year.length === 4 ? `${year}-01-01` : null,
      total_mileage: values.total_mileage === '' ? null : values.total_mileage,
      engine_power: blankToNull(values.engine_power),
      number_of_previous_owners: blankToNull(values.number_of_previous_owners),
      capacity: blankToNull(values.capacity),
      drive_type: emptyToNull(drive_type),
      gear_box: emptyToNull(gear_box),
      next_hu: monthPayload(next_hu)
    })
  }

  const applyPurchasedOffer = (form: FormApi<VehicleCreateFormValues>, offer: MarketplaceOffer): void => {
    const marketPlaceBrandId = brands.find((brand) => brand.rails_app_id === offer.brand_id)?.id ?? 0
    form.change('model', offer.model ?? '')
    form.change('vin', offer.vin ?? '')
    form.change('year', yearFromDate(offer.first_registration))
    form.change('total_mileage', offer.total_mileage === null ? '' : offer.total_mileage.toString())
    form.change('engine_power', offer.engine_power === null ? '' : offer.engine_power.toString())
    form.change(
      'number_of_previous_owners',
      offer.number_of_previous_owners === null ? '' : offer.number_of_previous_owners.toString()
    )
    form.change('rails_app_id', offer.id.toString())
    form.change('rails_app_hash_id', offer.hash_id)
    form.change('motorbike_name', `${offer.brand_name} ${offer.model}`.trim())
    form.change('brand_id', marketPlaceBrandId)
    setSelectedOffer(offer)
  }

  const clearPurchasedOffer = (form: FormApi<VehicleCreateFormValues>): void => {
    form.change('model', '')
    form.change('vin', '')
    form.change('year', '')
    form.change('total_mileage', '')
    form.change('engine_power', '')
    form.change('number_of_previous_owners', '')
    form.change('rails_app_id', null)
    form.change('rails_app_hash_id', null)
    form.change('motorbike_name', null)
    form.change('brand_id', 0)
    setSelectedOffer(null)
  }

  return (
    <Form
      onSubmit={handleSubmitForm}
      validate={validateCreateForm}
      initialValues={{
        ...initialValues,
        year: yearFromDate(initialValues.first_registration),
        total_mileage: initialValues.total_mileage ?? '',
        capacity: initialValues.capacity ?? '',
        engine_power: initialValues.engine_power ?? '',
        number_of_previous_owners: initialValues.number_of_previous_owners ?? '',
        drive_type: initialValues.drive_type ?? '',
        gear_box: initialValues.gear_box ?? '',
        next_hu: monthFormValue(initialValues.next_hu)
      }}
      render={({ handleSubmit, form, submitting, values, dirty }) => (
        <form
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onSubmit={handleSubmit}
          className="self-stretch flex-1 relative bg-backgrounds-bg-field-component rounded-lg inline-flex flex-col justify-start items-center overflow-hidden"
          style={{
            boxShadow:
              '0px 4px 8px 0px rgba(0,0,0,0.08), 0px 2px 4px 0px rgba(0,0,0,0.08), 0px 0px 0px 1px rgba(0,0,0,0.08)'
          }}>
          <MotorbikeModelsFetcher brandId={Number(values.brand_id)} brands={brands} />
          <div className="self-stretch h-px bg-borders-border-base" />
          <div className="w-full max-w-[1000px] min-w-[720px] px-4 pt-4 pb-6 flex-1 flex flex-col justify-start items-start gap-6 overflow-y-auto">
            <div className="text-zinc-900 text-xl font-bold font-Montserrat leading-7">
              {t('my_vehicles.vehicle_data')}
            </div>
            {mode === 'create' && (
              <div className="self-stretch flex flex-col justify-end items-start gap-4">
                <InsetSegmentedControl
                  value={source}
                  onChange={(nextSource) => {
                    if (nextSource === 'own') {
                      form.change('rails_app_id', null)
                      form.change('rails_app_hash_id', null)
                      setSelectedOffer(null)
                    }
                    setSource(nextSource)
                  }}
                  options={[
                    { value: 'purchased', label: t('my_vehicles.source_purchased') },
                    { value: 'own', label: t('my_vehicles.source_own') }
                  ]}
                />
              </div>
            )}
            {mode === 'create' && source === 'purchased' && (
              <PurchasedOffersPicker
                selectedOffer={selectedOffer}
                onSelect={(offer) => {
                  applyPurchasedOffer(form, offer)
                }}
                onClear={() => {
                  clearPurchasedOffer(form)
                }}
              />
            )}
            {(mode === 'edit' || source === 'own' || selectedOffer !== null) && (
              <div className="self-stretch grid grid-cols-2 gap-4">
                <Field name="brand_id">
                  {({ input, meta }) => (
                    <div className={fieldClass}>
                      <FormFieldLabel label={t('my_vehicles.brand')} />
                      <FormSelect
                        value={Number(input.value) === 0 ? '' : input.value.toString()}
                        onChange={(value) => {
                          input.onChange(value.length === 0 ? 0 : Number(value))
                          form.change('model', '')
                        }}
                        options={brandOptions}
                        placeholder={t('my_vehicles.choose')}
                        error={isFieldInvalid(meta)}
                      />
                      <FormFieldError show={isFieldInvalid(meta)} message={t('my_vehicles.field_required')} />
                    </div>
                  )}
                </Field>
                <Field name="model" defaultValue="">
                  {({ input, meta }) => (
                    <div className={fieldClass}>
                      <FormFieldLabel label={t('my_vehicles.model')} />
                      <FormAutocomplete
                        value={input.value ?? ''}
                        onChange={input.onChange}
                        options={motorbikeModels}
                        placeholder={t('my_vehicles.choose')}
                        error={isFieldInvalid(meta)}
                        disabled={Number(values.brand_id) === 0}
                      />
                      <FormFieldError show={isFieldInvalid(meta)} message={t('my_vehicles.field_required')} />
                    </div>
                  )}
                </Field>
                <Field name="year">
                  {({ input, meta }) => (
                    <div className={fieldClass}>
                      <FormFieldLabel label={t('my_vehicles.year')} />
                      <FormSelect
                        value={input.value}
                        onChange={input.onChange}
                        options={yearOptions}
                        placeholder={t('my_vehicles.choose')}
                        error={isFieldInvalid(meta)}
                      />
                      <FormFieldError show={isFieldInvalid(meta)} message={t('my_vehicles.field_required')} />
                    </div>
                  )}
                </Field>
                <Field name="total_mileage">
                  {({ input, meta }) => (
                    <div className={fieldClass}>
                      <FormFieldLabel label={t('my_vehicles.mileage')} />
                      <FormSuffixInput
                        value={input.value ?? ''}
                        onChange={input.onChange}
                        placeholder={t('my_vehicles.mileage_placeholder')}
                        suffix={t('my_vehicles.km')}
                        error={isFieldInvalid(meta)}
                      />
                      <FormFieldError show={isFieldInvalid(meta)} message={t('my_vehicles.field_required')} />
                    </div>
                  )}
                </Field>
                <Field name="vin">
                  {({ input, meta }) => (
                    <div className={`${fieldClass} col-span-2`}>
                      <FormFieldLabel
                        label={t('my_vehicles.vin')}
                        tooltip={t('my_vehicles.vin_tooltip')}
                        tooltipIcon={<InfoCircleSvg className="w-3.5 h-3.5 text-foregrounds-fg-muted" />}
                      />
                      <FormTextInput
                        value={input.value ?? ''}
                        onChange={input.onChange}
                        placeholder={t('my_vehicles.vin_placeholder')}
                        error={isFieldInvalid(meta)}
                      />
                      <FormFieldError show={isFieldInvalid(meta)} message={t('my_vehicles.field_required')} />
                    </div>
                  )}
                </Field>
                <IntegerField
                  name="capacity"
                  label={t('my_vehicles.promotion_capacity')}
                  suffix={t('my_vehicles.ccm')}
                />
                <IntegerField
                  name="engine_power"
                  label={t('my_vehicles.promotion_power')}
                  suffix={t('my_vehicles.kw')}
                />
                <IntegerField name="number_of_previous_owners" label={t('my_vehicles.promotion_owners')} />
                <div aria-hidden="true" />
                <VehicleEquipmentFields />
              </div>
            )}
          </div>
          <FormFooter
            cancelLabel={t('my_vehicles.discard')}
            onCancel={onCancel}
            submitLabel={mode === 'edit' ? t('my_vehicles.save') : t('my_vehicles.add')}
            submitDisabled={
              submitting || (mode === 'create' && source === 'purchased' && !hasPurchasedOfferId(values.rails_app_id))
            }
            {...(mode === 'create' ? { submitIcon: <PlusSvg className="w-3.5 h-3.5 text-icon-primary" /> } : {})}
            contentClassName="max-w-[1000px] min-w-[720px]"
            isDirty={dirty}
          />
        </form>
      )}
    />
  )
}

export default VehicleCreateForm
